import { Dialog } from "@/components-v2/Dialog";
import { TMessage } from "@/i18n/tt";
import { ButtonHTMLAttributes, forwardRef, useMemo, useState } from "react";
import { MTextInput } from "@/components-v2/TextInput";
import { ApiService } from "@/api";
import { failure, isOk, isPending, pending, Portfolio, Result } from "@/common/common";
import {
  CompanySelectorModel,
  portfolioAllCompanies,
  portfolioMyInvestments,
  PortfolioSettingsActions,
  PortfolioSettingsState,
  portfolioWithAdminRole,
  portfolioWithBoardRole,
} from "./portfolioDomain";
import { observer } from "mobx-react";
import { Box, VBox } from "@/components-v2/Box";
import { MCheckboxGroup } from "@/components-v2/Checkbox";
import { MSelect } from "@/components-v2/Select";
import { HighlightText } from "@/text/HighlightText";
import { EmptyState } from "@/components-v2/EmptyState";
import { Icon } from "@/components-v2/Icon";
import { AppBarData } from "@/appBar/appBarDomain";
import { DropdownMenu, DropdownMenuItemType } from "@/components-v2/Dropdown";
import { classNames } from "@/utils/classNames";
import { runInAction } from "mobx";
import { Button, LinkButton } from "@/components-v2/Button";
import { Alert } from "@/components-v2/Alert";
import { useRootStore, useTranslation, useUserStore } from "@/stores/RootStoreContext";
import { navigate, showVerifyEmailDialog } from "@/events/eventsDomain";
import { userSettingsUrl } from "@/utils/navigation";
import { StringFieldModel } from "@/common/commonForms";

type NewPortfolioDialogProps = {
  apiService: ApiService;
  onSuccess?: (portfolio: Portfolio) => void;
  onClose?: () => void;
};

export const NewPortfolioDialog = observer((props: NewPortfolioDialogProps) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<Result<TMessage, TMessage> | undefined>(undefined);
  const model = useMemo(() => new StringFieldModel({ required: true, validateEmpty: true }), []);
  const submit = () => {
    model.commit();
    if (model.validate()) {
      setResult(pending());
      props.apiService
        .createPortfolio(model.validatedValue)
        .then((created) => runInAction(() => props.onSuccess?.(created)))
        .catch(() => {
          runInAction(() => setResult(failure({ key: "general-error" })));
        });
    }
  };
  return (
    <Dialog
      title={t("portfolio.create")}
      onCancel={props.onClose}
      onSubmit={submit}
      submitResult={result}
    >
      <VBox gap={"md"}>
        <Alert type={"info"} message={{ key: "open.beta.access.info" }} icon={"info"} />
        <MTextInput model={model} label={t("name")} disabled={isOk(result)} />
        <div className={"subtle text-sm"}>{t("portfolio.about.ownership")}</div>
      </VBox>
    </Dialog>
  );
});

export const ConfirmDissolveDialog = observer(
  ({
    state,
    actions,
    contactsAdminsUrl,
  }: {
    state: PortfolioSettingsState;
    actions: PortfolioSettingsActions;
    contactsAdminsUrl?: string;
  }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.dissolve-portfolio.title")}
        onCancel={actions.closeConfirmDissolvePortfolio}
        onSubmit={actions.dissolvePortfolio}
        submitResult={state.portfolioSettingsUpdateResult}
        submitLabel={t("confirm")}
      >
        <VBox gap={"md"}>
          <Alert
            type={"warning"}
            icon={"warning"}
            message={{
              key: "portfolio.portfolio-settings.warning-message",
              params: {
                portfolioName: state.portfolioSettingsModel.name.inputValue,
                url: contactsAdminsUrl,
              },
            }}
          />
          <p>{t("portfolio.dissolve-portfolio.description")}</p>
          <p>{t("sure-you-want-to-proceed")}</p>
        </VBox>
      </Dialog>
    );
  },
);

export const PortfolioSettingsDialog = observer(
  ({
    state,
    actions,
    contactsAdminsUrl,
    canDissolvePortfolio,
  }: {
    state: PortfolioSettingsState;
    actions: PortfolioSettingsActions;
    contactsAdminsUrl?: string;
    canDissolvePortfolio: boolean;
  }) => {
    const { t } = useTranslation();
    const formDisabled =
      isPending(state.portfolioSettingsUpdateResult) || isOk(state.portfolioSettingsUpdateResult);
    return (
      <Dialog
        title={t("portfolio.portfolio-settings")}
        onCancel={actions.closePortfolioSettings}
        actions={
          <Box justifyContent="space-between">
            {canDissolvePortfolio ? (
              <LinkButton
                color={"error"}
                label={t("portfolio.dissolve-portfolio")}
                icon="trash"
                onClick={actions.confirmDissolvePortfolio}
              />
            ) : (
              <div />
            )}
            <Box>
              <Button
                label={t("cancel")}
                variant="secondary"
                onClick={actions.closePortfolioSettings}
                disabled={formDisabled}
              />
              <Button
                variant={"primary"}
                disabled={formDisabled}
                label={t("save")}
                loading={isPending(state.portfolioSettingsUpdateResult)}
                onClick={actions.savePortfolioSettings}
              />
            </Box>
          </Box>
        }
      >
        <VBox gap={"md"}>
          {state.portfolioCreationInfo && <div>{t(state.portfolioCreationInfo)}</div>}
          <MTextInput
            model={state.portfolioSettingsModel.name}
            label={t("title") + "*"}
            disabled={formDisabled}
          />
          <Alert
            type={"warning"}
            icon={"warning"}
            message={{
              key: "portfolio.portfolio-settings.warning-message",
              params: {
                portfolioName: state.portfolioSettingsModel.name.inputValue,
                url: contactsAdminsUrl,
              },
            }}
          />
        </VBox>
      </Dialog>
    );
  },
);

export const InvalidPortfolioView = observer(() => {
  const { t } = useTranslation();
  return (
    <Box grow={"golden"} alignItems={"center"}>
      <EmptyState
        illustration={<Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />}
        title={t("portfolio.invalidIdTitle")}
        description={t("portfolio.invalidIdText")}
      />
    </Box>
  );
});

export const PortfolioSelector = observer(function PortfolioSelector({
  selectedPortfolioOption,
  actions,
  user,
  portfolios,
}: AppBarData & {
  maximizeDropdown?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      button={({ isOpen, props }) => (
        <PortfolioSelectorButton
          isOpen={isOpen}
          selectedPortfolioOption={selectedPortfolioOption || portfolioAllCompanies}
          userName={user.name}
          {...props}
        />
      )}
      items={[
        ...[
          portfolioAllCompanies,
          portfolioWithAdminRole,
          portfolioWithBoardRole,
          portfolioMyInvestments,
        ].map<DropdownMenuItemType>((portfolioSelectionOption) => ({
          type: "item",
          label: t(portfolioSelectionOption.label),
          icon: portfolioSelectionOption.icon,
          onSelect: () => actions.filterCompaniesInPortfolio?.(portfolioSelectionOption),
        })),
        { type: "separator" },
        { type: "label", label: t("portfolios") },
        ...(portfolios ?? []).map<DropdownMenuItemType>((portfolio) => ({
          type: "item",
          label: portfolio.name,
          icon: "suitcaseSimple",
          onSelect: () => actions.openPortfolio?.(portfolio),
        })),
        actions.openCreatePortfolioDialog && {
          type: "item",
          label: t("portfolio.new"),
          icon: "plus",
          onSelect: actions.openCreatePortfolioDialog,
        },
      ]}
    />
  );
});
const PortfolioSelectorButton = forwardRef<
  HTMLButtonElement,
  {
    selectedPortfolioOption: Required<AppBarData>["selectedPortfolioOption"];
    isOpen: boolean;
    userName: string;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(function PortfolioSelectorButton(
  { selectedPortfolioOption, isOpen, userName, className, ...props },
  ref,
) {
  const { t } = useTranslation();

  return (
    <button
      {...props}
      ref={ref}
      className={classNames(
        className,
        "-outline-offset-4",
        "flex",
        "items-center",
        "gap-2",
        "min-w-0",
      )}
    >
      <div className={"min-w-0"}>
        <div className={"font-semibold overflow-hidden text-ellipsis whitespace-nowrap"}>
          {t(selectedPortfolioOption.label)}
        </div>
        <div className={"text-sm overflow-hidden text-ellipsis whitespace-nowrap"}>{userName}</div>
      </div>
      <Icon name={isOpen ? "caret-up" : "caret-down"} />
    </button>
  );
});

export const IsEmailVerifiedAlert = observer(
  ({ isEmailVerified }: { isEmailVerified: boolean }) => {
    const { t } = useTranslation();
    const { user } = useUserStore();
    const { locationStr } = useRootStore();
    return !isEmailVerified ? (
      <Alert
        icon={"warning"}
        message={
          <Box justifyContent={"space-between"}>
            <Box>
              {t("portfolio.email-not-verified.text", { email: user?.email })}
              <LinkButton
                label={t("portfolio.email-not-verified.link")}
                onClick={[showVerifyEmailDialog]}
                color={"inherit"}
              />
            </Box>
            <LinkButton
              label={t("portfolio.email-not-verified.change-email")}
              onClick={[navigate(userSettingsUrl(locationStr))]}
              color={"inherit"}
            />
          </Box>
        }
        type={"warning"}
        paddingX={"md"}
        paddingY={"sm"}
      />
    ) : null;
  },
);

export const CompanySelector = observer(function CompanySelector({
  model,
  label,
}: {
  model: CompanySelectorModel;
  label: string;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      {model.hasPortfolios && (
        <MSelect icon={"suitcaseSimple"} label={label} model={model.portfolioFilter} />
      )}
      <MTextInput
        model={model.search}
        icon={"magnifying-glass"}
        placeholder={t("search")}
        type={"search"}
      />
      <Box alignItems={"center"} justifyContent={"space-between"}>
        <Box gap={"md"}>
          <LinkButton
            label={t("select-all")}
            disabled={model.selection.isAllSelected}
            onClick={() => model.selection.options.forEach((c) => model.selection.select(c))}
          />
          <LinkButton
            label={t("select-none")}
            disabled={model.selection.selected.length === 0}
            onClick={() => model.selection.options.forEach((c) => model.selection.unselect(c))}
          />
        </Box>
        <LinkButton
          label={
            model.showOnlySelected.value
              ? t("showAll")
              : t("portfolio.company.portfolio-project.show-selected-companies", {
                  count: model.selection.selectedIds.size,
                })
          }
          onClick={model.showOnlySelected.toggle}
        />
      </Box>
      <MCheckboxGroup
        model={model.selection}
        labelFn={(c) => (
          <Box>
            <HighlightText value={c.name} words={model.highlightWords} />
            <small className={"subtle"}>
              <HighlightText value={c.organisationNumber} words={model.highlightWords} />
            </small>
          </Box>
        )}
      />
    </VBox>
  );
});
